import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  DetailContainer,
  DetailHeader,
  BackButton,
  EntityHeader,
  EntityIcon,
  EntityInfo,
  ActionButtons,
  DetailContent,
  ItemsSection,
  ItemsList,
  ItemCard,
  ItemInfo,
  EmptyState,
  LearnMoreButton,
  EntityName,
  EntityDescription,
  ItemSectionTitle,
  ArrowLinkIconWrapper,
  ArrowLinkFilterIconWrapper
} from './EntityDetailPanel.styles';
import { HoldingEntitySvg } from '../EntityIcons';
import { ArrowIcon, ArrowLinkIcon, EditPencilIcon } from '../../assets/icons';
import { useStore } from '../../../app/data/root.store';
import { navigateToUrl } from '../../../../common/utils/navigation.utils';
import { ROUTES } from '../../../../common/constants/routes.const';
import { observer } from 'mobx-react';
import { ItemLogo } from '../../../../common/components/ItemLogo/ItemLogo';
import { CommonBadge } from '../../../../common/components/CommonBadge/CommonBadge';
import { ItemMoreInfos, MoreInfosItem } from '../../../wealth/pages/class/components/ClassPage.styles';
import { SecondaryButtonWithIcon } from '../../../../common/components/buttons/NewButtons.styles';
import { useAnalytics } from '../../../app/data/AnalyticsProvider';
import { createGoToFilterCTAClickEvent } from '../../data/entities.mixpanel-events';
import { startIntercomTourById } from '../../../../common/utils/intercom.utils';

export const EntityDetailPanel = observer(({ entity, onBack, onEdit }) => {
  const { filtersStore, wealthStore, metadataStore, createItemStore } = useStore();
  const history = useHistory();
  const { mixpanelTrack } = useAnalytics();

  const enhanceItemWithMetadata = (item) => {
    const category = metadataStore.categories?.find(cat => cat.id === item.categoryId);
    const assetClass = category ? metadataStore.classes?.find(cls => cls.id === category.classId) : null;

    let displayTitle = item.title || item.name || '';
    if (item.name && item.name !== item.title) displayTitle += `, ${item.name}`;
    if (item.connectedInstitutionName && !displayTitle.includes(item.connectedInstitutionName)) displayTitle += `, ${item.connectedInstitutionName}`;

    return {
      ...item,
      title: displayTitle,
      categoryClass: assetClass ? {
        id: assetClass.id,
        title: assetClass.title,
        color: assetClass.color,
        icon: assetClass.icon
      } : {
        id: 0,
        title: 'Other',
        color: '#E9EAEB',
        icon: 'other'
      },
      category: category ? {
        id: category.id,
        title: category.title
      } : {
        id: 0,
        title: 'Other'
      }
    };
  };

  const items = (entity?.items || []).map(enhanceItemWithMetadata);
  const totalItems = items.length;

  const handleGoToFilter = async () => {
    mixpanelTrack('CTA Clicked', createGoToFilterCTAClickEvent(entity.name, entity.type));
    await filtersStore.fetchData();
    filtersStore.handleClearFilterSelections();
    const holdingEntityParameter = filtersStore.parametersObj['holdingEntity'];
    if (holdingEntityParameter?.options) {
      const entityOption = holdingEntityParameter.options.find(
        option => option.value === entity.name || option.label === entity.name
      );
      if (entityOption) {
        entityOption.isSelected = true;
        holdingEntityParameter.selectedCount = 1;
        const filterHashParam = filtersStore.setAppliedFiltersFromSelection();
        navigateToUrl(history, `${ROUTES.wealthOverview.url}?filters=${filterHashParam}`, false, true);
        wealthStore.refreshWealthData(false, true);
        metadataStore.fetchData();
      }
    }
  };

  const handleLearnBulkEdit = () => {
    startIntercomTourById('544685') 
  };

  const handleItemClick = (item) => {
       if ('in process' === item.llmProcessingStatus) {
      createItemStore.openModalForSpecificCategory(item.categoryClass.id, item.categoryId, { title: item.title, name: item.name, value: +item.value, cashInvested: +item.value, currency: item.currency, oldAssetId: +item.id, llmProcessingStatus: 'done', isMagicBoxAi: true, remarks: 'Automatically generated from an uploaded file by AI.' });
    } else {
      if (item.id && item.categoryClass?.title) {
        const classTitle = item.categoryClass.title.toLowerCase().replace(/\s+/g, '-');
        navigateToUrl(history, `/wealth/assets/${classTitle}/${item.id}/overview`);
      }
    }
  };

  return (
    <DetailContainer>
      <div className="back-section">
        <BackButton onClick={onBack}>
          <ArrowIcon />
          Back to entity list
        </BackButton>
      </div>

      <DetailContent>
        <DetailHeader>
          <EntityHeader>
            <EntityIcon color={entity.color_code || entity.colorCode}>
              <HoldingEntitySvg width={24} height={24} color="#1A1B1D" />
            </EntityIcon>
            <EntityInfo>
              <EntityName>{entity.name}</EntityName>
              <EntityDescription>{entity.type} • {totalItems} items</EntityDescription>
            </EntityInfo>
            <EntityDescription>{entity.description}</EntityDescription>
          </EntityHeader>

          <ActionButtons>
            <SecondaryButtonWithIcon onClick={() => onEdit(entity)}>
              <EditPencilIcon />
              Edit entity
            </SecondaryButtonWithIcon>

            <SecondaryButtonWithIcon onClick={handleGoToFilter} style={{ gap: '4px' }}>
              Go to filter
              <ArrowLinkFilterIconWrapper>
                <ArrowLinkIcon />
              </ArrowLinkFilterIconWrapper>
            </SecondaryButtonWithIcon>
          </ActionButtons>
        </DetailHeader>
        {totalItems === 0 ? (
          <EmptyState>
            <div className="empty-title">This entity doesn’t own any items yet</div>
            <LearnMoreButton onClick={handleLearnBulkEdit}>
              Learn about bulk edit
              <ArrowLinkIcon />
            </LearnMoreButton>
          </EmptyState>
        ) : (
          <>
            <ItemsSection>
              <ItemSectionTitle>Items ({totalItems})</ItemSectionTitle>
              <ItemsList>
                {items.map((item, index) => (
                  <ItemCard
                    key={`item-${item.id || index}`}
                    onClick={() => handleItemClick(item, 'item')}
                  >
                    <ItemLogo item={item} />
                    <ItemInfo>
                      <div className="item-name">
                        {item.title || item.name}
                        {item.isClosed && <CommonBadge badgeType="closed" noMargin />}
                        {item.connectedProvider === 'vip' && <CommonBadge badgeType="vipService" noMargin />}
                        {item.ownershipPercentage && (
                          <CommonBadge
                            title={`${item.ownershipPercentage}%`}
                            badgeWrapperStyle={{
                              background: '#DDE3EB',
                              color: '#585D66',
                              borderRadius: '2px 0px 0px 2px',
                              padding: '1px 1px 0px 4px',
                              height: '16px'
                            }}
                            noMargin
                          />
                        )}
                      </div>
                      <ItemMoreInfos>
                        <MoreInfosItem>
                          {item.category?.title || 'Item'}
                        </MoreInfosItem>
                      </ItemMoreInfos>
                    </ItemInfo>
                    <ArrowLinkIconWrapper>
                      <ArrowLinkIcon />
                    </ArrowLinkIconWrapper>
                  </ItemCard>
                ))}
              </ItemsList>
            </ItemsSection>
          </>
        )}
      </DetailContent>
    </DetailContainer>
  );
});